import React, { type FC } from 'react';
import { ArrowBack } from '@mui/icons-material';
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';

interface VerifyEmailFormProps {
  content: typeof import('./translations.json')['en'];
  email: string;
  code: string;
  error: string | null;
  loading: boolean;
  onBack: () => void;
  onCodeChange: (code: string) => void;
  onSubmit: () => Promise<void>;
}

const VerifyEmailForm: FC<VerifyEmailFormProps> = ({
  content,
  email,
  code,
  error,
  loading,
  onBack,
  onCodeChange,
  onSubmit,
}) => {
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await onSubmit();
  };

  return (
    <>
      <Button
        startIcon={<ArrowBack />}
        onClick={onBack}
        disabled={loading}
        sx={{ mb: 1, color: '#90caf9', textTransform: 'none' }}
      >
        {content.back}
      </Button>

      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: '#ffffff' }}>
        {content.verificationTitle}
      </Typography>
      <Typography variant="body2" sx={{ color: '#94a3b8', mb: 4 }}>
        {content.verificationDescription}
      </Typography>

      {error && (
        <Typography variant="body2" sx={{ color: '#ef4444', mb: 2, fontWeight: 500 }}>
          {error}
        </Typography>
      )}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Typography variant="body2" sx={{ color: '#ffffff', mb: 0.5 }}>
          {content.email}
        </Typography>
        <Typography variant="body2" sx={{ color: '#94a3b8', mb: 3 }}>
          {email}
        </Typography>

        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#ffffff' }}>
          {content.verificationCode}
        </Typography>
        <TextField
          fullWidth
          id="verification-code"
          name="code"
          autoComplete="one-time-code"
          autoFocus
          value={code}
          onChange={(event) => onCodeChange(event.target.value)}
          disabled={loading}
          inputProps={{
            maxLength: 6,
            inputMode: 'numeric',
            style: { textAlign: 'center', fontSize: '1.25rem', letterSpacing: '0.4rem' },
          }}
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
              borderRadius: '8px',
              color: '#ffffff',
              '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
              '&:hover fieldset': { borderColor: '#1976d2' },
              '&.Mui-focused fieldset': { borderColor: '#1976d2' },
            },
          }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disableElevation
          size="large"
          disabled={loading || !code.trim()}
          sx={{
            mb: 4,
            fontSize: '15px',
            fontWeight: 600,
            py: 1.5,
            borderRadius: '8px',
            backgroundColor: '#1976d2',
            color: '#ffffff',
            '&:hover': { backgroundColor: '#115293' },
            '&.Mui-disabled': {
                backgroundColor: 'rgba(255, 255, 255, 0.12)',
                color: 'rgba(255, 255, 255, 0.3)',
            }
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : content.verify}
        </Button>
      </Box>
    </>
  );
};

export default VerifyEmailForm;
